import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel,
  SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarClock, Plus, Trash2, Pause, Play, Loader2 } from "lucide-react";
import { formatCurrency, formatDate } from "@/lib/format";

/**
 * RecurringTransfersDialog (iter 72r)
 *
 * Manage auto-scheduled transfers. Users define frequency + start_date and
 * a background loop (server.py `_recurring_transfers_loop`) fires them
 * every 30 minutes when due.
 *
 * Endpoints:
 *   GET    /api/recurring-transfers
 *   POST   /api/recurring-transfers
 *   PATCH  /api/recurring-transfers/:id   { active?: bool, end_date?: str }
 *   DELETE /api/recurring-transfers/:id
 */
const FREQ_LABEL = { weekly: "Weekly", biweekly: "Bi-weekly", monthly: "Monthly" };

function encode(k, id) { return `${k}:${id}`; }
function decode(v) {
  if (!v) return null;
  const i = v.indexOf(":");
  return { kind: v.slice(0, i), id: v.slice(i + 1) };
}

export default function RecurringTransfersDialog({ open, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [investments, setInvestments] = useState([]);
  // Create form
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [fromVal, setFromVal] = useState("");
  const [toVal, setToVal] = useState("");
  const [amount, setAmount] = useState("");
  const [frequency, setFrequency] = useState("monthly");
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState("");
  const [memo, setMemo] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: accs }, { data: invs }] = await Promise.all([
        api.get("/recurring-transfers"),
        api.get("/accounts"),
        api.get("/investments"),
      ]);
      setItems(list || []);
      setAccounts(accs || []);
      setInvestments(invs || []);
    } catch (e) { /* non-blocking */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const resetForm = () => {
    setName(""); setFromVal(""); setToVal(""); setAmount("");
    setFrequency("monthly"); setEndDate(""); setMemo("");
    setStartDate(new Date().toISOString().slice(0, 10));
    setCreating(false);
  };

  const submit = async () => {
    if (!name.trim()) return toast.error("Give this schedule a short name");
    const from = decode(fromVal), to = decode(toVal);
    if (!from || !to) return toast.error("Pick both a source and a destination");
    if (from.kind === to.kind && from.id === to.id)
      return toast.error("Source and destination must differ");
    const amt = parseFloat(amount);
    if (!(amt > 0)) return toast.error("Amount must be positive");
    setSaving(true);
    try {
      await api.post("/recurring-transfers", {
        name: name.trim(), from, to,
        amount: amt, frequency, start_date: startDate,
        end_date: endDate || null, memo,
      });
      toast.success(`"${name.trim()}" scheduled · next run ${startDate}`);
      resetForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save schedule");
    } finally { setSaving(false); }
  };

  const toggleActive = async (r) => {
    try {
      await api.patch(`/recurring-transfers/${r.id}`, { active: !r.active });
      toast.success(r.active ? "Paused" : "Resumed");
      load();
    } catch (err) { toast.error("Could not update"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name}"? Already-posted transfers will remain.`)) return;
    try {
      await api.delete(`/recurring-transfers/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error("Delete failed"); }
  };

  const pickerOptions = () => (
    <>
      <SelectGroup>
        <SelectLabel>Wallets & Credit Cards</SelectLabel>
        {accounts.map((a) => (
          <SelectItem key={a.id} value={encode("wallet", a.id)}>{a.name} · {a.currency}</SelectItem>
        ))}
      </SelectGroup>
      {investments.length > 0 && (
        <SelectGroup>
          <SelectLabel>Investments</SelectLabel>
          {investments.map((i) => (
            <SelectItem key={i.id} value={encode("investment", i.id)}>{i.symbol || i.name} · {i.currency}</SelectItem>
          ))}
        </SelectGroup>
      )}
    </>
  );

  const findLabel = (side) => {
    if (!side) return "—";
    const list = side.kind === "wallet" ? accounts : investments;
    const rec = list.find((x) => x.id === side.id);
    return rec ? `${rec.name || rec.symbol} · ${rec.currency}` : side.id;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent data-testid="recurring-transfers-dialog" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="w-4 h-4" /> Recurring transfers
          </DialogTitle>
          <DialogDescription>
            Schedule an auto-transfer (e.g. every payday move some cash to
            savings). The system checks for due schedules every 30 minutes and
            posts them for you.
          </DialogDescription>
        </DialogHeader>

        {/* List */}
        <div className="border border-[var(--divider)] rounded-lg overflow-hidden" data-testid="recurring-list">
          <div className="grid grid-cols-[1fr_1fr_100px_120px_120px_100px] gap-3 px-3 py-2 bg-[var(--hover)] text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
            <div>Name</div>
            <div>From → To</div>
            <div>Frequency</div>
            <div>Amount</div>
            <div>Next run</div>
            <div className="text-right">Actions</div>
          </div>
          {loading && items.length === 0 && (
            <div className="p-6 text-center text-xs text-[var(--muted-text)]">Loading…</div>
          )}
          {!loading && items.length === 0 && (
            <div className="p-6 text-center text-xs text-[var(--muted-text)]" data-testid="recurring-empty">
              No recurring transfers yet — set one up below.
            </div>
          )}
          {items.map((r) => (
            <div key={r.id} data-testid={`recurring-row-${r.id}`}
                 className="grid grid-cols-[1fr_1fr_100px_120px_120px_100px] gap-3 px-3 py-2 border-t border-[var(--divider)] items-center text-sm">
              <div className="font-medium truncate">
                {r.name}
                {!r.active && <span className="ml-2 text-[9px] uppercase tracking-widest text-[#B4283A]">paused</span>}
              </div>
              <div className="text-xs text-[var(--muted-text)] truncate">
                {findLabel(r.from)} → {findLabel(r.to)}
              </div>
              <div className="text-xs">{FREQ_LABEL[r.frequency] || r.frequency}</div>
              <div className="font-mono">{formatCurrency(r.amount, r.from?.currency || "USD")}</div>
              <div className="text-xs">{formatDate(r.next_run_at)}</div>
              <div className="text-right flex gap-1 justify-end">
                <Button variant="ghost" size="sm" onClick={() => toggleActive(r)}
                        data-testid={`recurring-toggle-${r.id}`}
                        title={r.active ? "Pause" : "Resume"}>
                  {r.active ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => remove(r)}
                        className="text-[#B4283A] hover:bg-[#FCE7E9]"
                        data-testid={`recurring-delete-${r.id}`}
                        title="Delete">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Create form */}
        {!creating && (
          <div className="mt-2">
            <Button variant="outline" onClick={() => setCreating(true)} data-testid="recurring-add-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> New recurring transfer
            </Button>
          </div>
        )}
        {creating && (
          <div className="mt-2 p-4 rounded-lg border border-[var(--divider)] bg-[var(--card)] space-y-3" data-testid="recurring-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)}
                       placeholder="e.g. Payday savings 10%"
                       data-testid="recurring-name" />
              </div>
              <div>
                <Label>Frequency</Label>
                <Select value={frequency} onValueChange={setFrequency}>
                  <SelectTrigger data-testid="recurring-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Bi-weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>From</Label>
                <Select value={fromVal} onValueChange={setFromVal}>
                  <SelectTrigger data-testid="recurring-from"><SelectValue placeholder="Pick source" /></SelectTrigger>
                  <SelectContent>{pickerOptions()}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>To</Label>
                <Select value={toVal} onValueChange={setToVal}>
                  <SelectTrigger data-testid="recurring-to"><SelectValue placeholder="Pick destination" /></SelectTrigger>
                  <SelectContent>{pickerOptions()}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Amount</Label>
                <Input type="number" step="0.01" min="0" value={amount}
                       onChange={(e) => setAmount(e.target.value)}
                       data-testid="recurring-amount" className="font-mono" />
              </div>
              <div>
                <Label>First run</Label>
                <Input type="date" value={startDate}
                       onChange={(e) => setStartDate(e.target.value)}
                       data-testid="recurring-start" />
              </div>
              <div>
                <Label>End date (optional)</Label>
                <Input type="date" value={endDate}
                       onChange={(e) => setEndDate(e.target.value)}
                       data-testid="recurring-end" />
              </div>
            </div>
            <div>
              <Label>Memo (optional)</Label>
              <Input value={memo} onChange={(e) => setMemo(e.target.value)}
                     data-testid="recurring-memo"
                     placeholder="Auto-attached to each posted transfer" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={resetForm}>Cancel</Button>
              <Button onClick={submit} disabled={saving}
                      className="btn-brand" data-testid="recurring-save">
                {saving
                  ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Saving…</>
                  : "Schedule it"}
              </Button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
